import React, { useContext, useEffect, useState } from 'react';

import { Resizable } from 're-resizable';
import styled from 'styled-components';
import { FileContext } from '../../../context/FileContext';
import CalendarIcon from '../../../assets/icons/datepicker.svg';

import { DatePicker } from '@vibe/core/next';
import DialogContentContainer from 'monday-ui-react-core/dist/DialogContentContainer';

import RequiredIcon from '../../../assets/icons/astrik.svg';
import { useColumnOrder } from '../../../hooks/useColumnOrder';

const DatePickerContainer = styled.div`
  width: 100%;
  height: 100%;
  border: 1px solid #c5c7d0;
  border-radius: 4px;
  display: flex;
  align-items: center;
  padding: 2px 10px;
  color: #676879;
  background: ${({ color }) => (color ? color : 'white')};
  img {
    margin: 0 0 0 auto;
  }
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  column-gap: 8px;
  margin-left: auto;
  justify-content: right;
  img.required {
    position: absolute;
    top: 4px;
    right: 4px;
  }
`;
const DateSelctorUiContainer = styled.div`
  table {
    border: none !important;
  }

  th,
  .th,
  td,
  .td {
    box-shadow: none;
  }
  .CalendarDay__default {
    border-top: none;
    border-bottom: none;
  }
`;
const OuterContainer = styled.div`
  padding: 2px;
  border: 4px solid transparent !important;
  border-radius: 4px !important;

  &.active {
    border: 4px solid rgba(0, 115, 234, 0.4) !important;
    border-radius: 4px !important;
  }

  &.hover-active:hover {
    border: 4px solid rgba(0, 115, 234, 0.4) !important;
    border-radius: 4px !important;
  }
`;

const MIN_WIDTH = 50;
const MIN_HEIGHT = 15;

const Img = styled.img`
  position: absolute;

  height: ${({ height }) =>
    height > MIN_HEIGHT ? MIN_HEIGHT + 'px' : height + 'px'};
  top: '4px';
  left: ${({ height, width, fontSize }) =>
    height < 20 || width < MIN_WIDTH + 10 ? '2px' : width - 30 + 'px'};
  font-size: ${({ fontSize }) => fontSize + 'px'};
  width: fit-content;
`;

const DatePickerField = ({ item, setIsResizing, editMode = false }) => {
  const defaultHeight = 18.33;
  const defaultWidth = 150;
  const defaultFontSize = 11;

  const {
    addedFormFields,
    updateFormFields,
    currentSelectedItem,
    draggableFieldsData,
  } = useContext(FileContext);

  const { rgbMapper } = useColumnOrder();

  const [fontSize, setFontSize] = useState(item.fontSize || defaultFontSize);
  const [showPicker, setShowPicker] = useState(false);
  const [previewDate, setPreviewDate] = useState(undefined);
  const itemColor = item.color ?? item.signer?.color;
  const backgroundColor = itemColor
    ? rgbMapper[itemColor] ?? itemColor
    : '#E6E6FA';

  useEffect(() => {
    if (!item?.width) item.width = defaultWidth;
    if (!item?.height) item.height = defaultHeight;
  }, []);

  const calc = (d, item) => {
    const newHt = item.height + d.height;
    const newWt = item.width + d.width;

    const fontRH = parseInt(newHt) - parseInt(newHt) * 0.4;

    const fontRW = Number(newWt) / 10;
    const fontSize = Math.min(fontRH, fontRW);

    return fontSize > 10 ? fontSize : 11;
  };

  const handleResizeStart = (_e, _dir) => {
    setIsResizing(true);
  };

  const handleResizeEnd = (item) => (_e, _dir, _ref, d) => {
    setIsResizing(false);

    // calc(d, item);
    // check min height width

    const newHt = item.height + d.height;
    const newWt = item.width + d.width;

    if (newHt > newWt) {
      return;
    }
    if (item.width + d.width >= MIN_WIDTH) {
      item.width += d.width;
    } else {
      item.width = MIN_WIDTH;
    }

    if (item.height + d.height >= MIN_HEIGHT) {
      item.height += d.height;
    } else {
      item.height = MIN_HEIGHT;
    }

    addedFormFields[currentSelectedItem.current] = item;
    draggableFieldsData[item.itemId] = {
      height: item.height,
      width: item.width,
      fontSize: item.fontSize,
    };
    updateFormFields([...addedFormFields]);
  };

  const handleResize = (e, _dir, _ref, d) => {
    setIsResizing(true);
    const newHt = item.height + d.height;
    const newWt = item.width + d.width;

    if (newHt > newWt) {
      return;
    }
    // calc(d, item);

    const fontSize = calc(d, item);

    setFontSize(fontSize);
    item.fontSize = fontSize;
    addedFormFields[currentSelectedItem.current] = item;
    updateFormFields([...addedFormFields]);
  };

  return (
    <>
      <OuterContainer
        className={item.isActive ? `active` : `hover-active`}
        fontSize={fontSize}
      >
        <Resizable
          size={{
            height: item?.height || defaultHeight,
            width: item?.width || defaultWidth,
          }}
          lockAspectRatio={false}
          enable={{
            top: false,
            right: item?.isActive,
            bottom: item?.isActive,
            left: false,
            topRight: false,
            bottomRight: item?.isActive,
            bottomLeft: false,
            topLeft: false,
          }}
          className={
            item.isActive
              ? 'resizeable resizable-min-dimensions'
              : 'resizable-min-dimensions'
          }
          onResizeStart={handleResizeStart}
          onResizeStop={handleResizeEnd(item)}
          onResize={handleResize}
        >
          <DatePickerContainer
            color={backgroundColor}
            onClick={() => setShowPicker((prev) => !prev)}
          >
            {item.width > MIN_WIDTH + 40 && item.height > MIN_HEIGHT + 4 ? (
              <p>--Pick a date--</p>
            ) : null}
            <IconContainer>
              <Img
                className="date-icon"
                src={CalendarIcon}
                alt="date picker"
                height={item.height}
                width={item.width}
                fontSize={
                  fontSize < 15
                    ? 15
                    : fontSize > item.height && item.height < 24
                    ? item.height - 4
                    : fontSize > 20
                    ? 20
                    : 20
                }
              />
              {item.required ? (
                <img
                  className="required"
                  src={RequiredIcon}
                  alt=""
                  width={10}
                />
              ) : null}
            </IconContainer>
          </DatePickerContainer>
        </Resizable>
      </OuterContainer>
      {!editMode && showPicker ? (
        <DateSelctorUiContainer>
          <DialogContentContainer className="DatePicker-stories-module_datepickerDialogContentContainer">
            <DatePicker
              data-testid="date-picker"
              date={previewDate}
              onDateChange={setPreviewDate}
            />
          </DialogContentContainer>
        </DateSelctorUiContainer>
      ) : null}
    </>
  );
};

export default DatePickerField;
